import { StyledTable, StyledTableRow } from '@/shared/ui/tables'
import React, { useState, useEffect, useRef } from 'react'
import {
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Tabs,
    Tab,
    Box,
} from '@mui/material'
import Loading from '@/shared/composed-ui/LoadingData'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { faExclamationCircle } from '@fortawesome/pro-duotone-svg-icons/faExclamationCircle'

import DisplayItem from '../../DisplayItems/DisplayItem'
import DossierModalEditButton from '@/feature/ContentDossier/DossierModalEditButton'
import BlockCardPerson from './BlockCardPerson'
import BlockCardCarousel from './BlockCardCarousel'
import BlockCardDefault from './BlockCardDefault'
import BlockCardTimeline from './BlockCardTimeline'
import DossierModalNewButton from '@/feature/ContentDossier/DossierModalNewButton'
import Pagination from '@/shared/ui/pagination/Pagination'
import EventCalendar from '../MyPage/EventCalendar'
import { LoadingDefault } from '@/shared/ui/loading'
import BlockToolbar from './BlockToolbar'
import { default as ContentDossierIndex } from '../../../feature/ContentDossier/Index/ContentDossierIndex.tsx'
import { useSocketService } from '../../../context/SocketServiceContext.tsx'
import { toast } from 'react-toastify'
import { isInteractiveClick } from '../../../util/clickGuards'
import { horizontalScrollableTabsSx } from '@/shared/ui/styles'

const Block = (props) => {
    const contentClickSelector =
        '.contentData, .displayItem, .list-group-item, .badge'
    const interactiveClickSelector = [
        'a',
        'button',
        'input',
        'textarea',
        'select',
        'option',
        'label',
        '[role="button"]',
        '[role="menuitem"]',
        '[contenteditable="true"]',
        '.MuiButtonBase-root',
        '.dossier-inline-edit',
    ].join(', ')

    const socketService = useSocketService()
    const kedo = props.kedo
    const curRef = useRef(null)

    const [contents, setContents] = useState(null)
    const [query, setQuery] = useState(
        props.block &&
            props.block.settings?.tab_queries?.length > 0 &&
            props.block.settings.tab_queries[0]
            ? props.block.settings.tab_queries[0].query
            : null
    )
    const [add, setAdd] = useState(false)
    const [page, setPage] = useState(0)
    const [loading, setLoading] = useState(false)
    const [pager, setPager] = useState(null)
    const [pageError, setPageError] = useState(false)

    const [activeTab, setActiveTab] = useState(0)

    const toastOptions = {
        position: 'bottom-center',
        autoClose: 2500,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
    }

    const getCurrentRef = () => {
        return curRef
    }

    const fetchData = () => {
        let environment = kedo.env().getEnvironment()
        if (!environment) {
            return
        }

        let params = {
            params: {
                defDossier: props.block.def_dossier_id,
                environment: environment.id,
                page: page,
                limit:
                    props.block.settings?.limit?.length > 0
                        ? props.block.settings.limit
                        : 10,
            },
        }
        if (props.block.settings && props.block.settings.view_name) {
            params.params.view = props.block.settings.view_name
        }

        if (
            props.block.settings?.sort?.length > 0 &&
            Array.isArray(props.block.settings.sort)
        ) {
            params.params.sort = []
            params.params.sortOrder = []
            props.block.settings.sort.map((filterItem) => {
                params.params.sort.push(filterItem.dddfId)
                params.params.sortOrder.push(filterItem.sort)
            })
        }

        if (props.link) {
            params.params.linkId = props.link
            params.params.embedded =
                props.dossier && props.dossier.def_dossier_id
                    ? props.dossier.id
                    : props.dossier
        }

        if (query) {
            params.params.dossierQuery = query
        }

        if (props.block.dossier_query_id) {
            params.params.dossierQuery = props.block.dossier_query_id
        }

        setLoading(true)

        kedo.api()
            .get(kedo.api().getContentDossierEndpoint(), params)
            .then((response) => {
                setContents(response.data.results)
                setPager(response.data.pager)
                setLoading(false)
            })
            .catch((error) => {
                setLoading(false)
                throw error
            })
    }

    useEffect(() => {
        fetchData()
    }, [page])

    const onSuccess = (dossierId) => {
        setTimeout(() => {
            socketService.displayItem.update({
                dossierId: dossierId,
                sender: socketService.socket().id,
                displayItem: props.block.id,
            })

            if (curRef && curRef.current) {
                curRef.current.fetchNewDossier(dossierId)
            }
        }, 100)
        setAdd(false)
    }

    const loadSocketCallbacks = () => {
        socketService.displayItem.refreshListener((data) => {
            if (
                socketService.socket().id !== data.sender.id &&
                data.displayItem === props.block.id
            ) {
                if (curRef && curRef.current) {
                    curRef.current.fetchNewDossier(data.dossierId)
                }

                if (kedo.user().getUserId() !== data.sender.userId) {
                    toast.success(
                        props.kedo.t('dossier_errors.success_create_ws', {
                            username: data.sender.username,
                        }),
                        {
                            ...toastOptions,
                            toastId: `dossier_errors.success_create_ws_${data.dossierId}`,
                        }
                    )
                }
            }
        })
    }

    loadSocketCallbacks()

    useEffect(() => {
        return () => {
            socketService.displayItem.removeDdiListeners()
        }
    }, [])

    const renderData = (data) => {
        if (loading) {
            return <Loading />
        }

        let view = props.block.settings.view_name
            ? props.block.settings.view_name
            : 'list'
        let listItems = contents
            ? contents.items.filter((item) => item.view === view)
            : []
        let layout = props.block.settings.layout
            ? props.block.settings.layout
            : 'list'
        let template = props.block.settings.template
            ? props.block.settings.template
            : 'list'

        let blockProps = {
            pager: pager,
            getValue: getValue,
            kedo: kedo,
            listItems: listItems,
            data: data,
            block: props.block,
            // nextPage: nextPage,
            // prevPage: prevPage,
            handleContent: handleContent,
        }

        if (layout === 'cards') {
            switch (template) {
                case 'cardCarousel':
                    return <BlockCardCarousel {...blockProps} />
                case 'cardPerson':
                    return <BlockCardPerson {...blockProps} />
                case 'cardTimeline':
                    return <BlockCardTimeline {...blockProps} />
                default:
                    return <BlockCardDefault {...blockProps} />
            }
        }

        if (layout === 'calendar') {
            return (
                <EventCalendar
                    {...blockProps}
                    templateOptions={
                        props.block.settings.templateOptions
                            ? props.block.settings.templateOptions
                            : null
                    }
                />
            )
        }

        const { settingsLink, block } = props || {}
        const shouldShowAdd = settingsLink
            ? !settingsLink.hide_add
            : Boolean(block?.settings?.show_add)

        return (
            <div>
                {loading ? (
                    <center>
                        <LoadingDefault />
                    </center>
                ) : props.misc ? (
                    <>
                        {shouldShowAdd ? (
                            <Box display="flex" justifyContent="flex-end" p={1}>
                                <DossierModalNewButton
                                    location={'Block'}
                                    defDossierId={props.block.def_dossier_id}
                                    onSuccess={onSuccess}
                                    onClose={() => setAdd(false)}
                                    add={add}
                                    onMax={props.onMax}
                                    size={'md'}
                                    kedo={kedo}
                                />
                            </Box>
                        ) : null}
                        <TableContainer component={Paper}>
                            <StyledTable size="small" striped>
                                {renderTableHead(contents, listItems)}
                                {renderTableContent(contents, listItems)}
                            </StyledTable>
                        </TableContainer>
                    </>
                ) : (
                    <ContentDossierIndex
                        props={{
                            ...props,
                            defDossierId: props.block.def_dossier_id,
                            dossierQuery: query
                                ? query
                                : props.block.dossier_query_id,
                            mode: 'block',
                            showEmail: false,
                            view: props.block.settings.view_name,
                            onClick: (dossier) => {
                                window.location.href =
                                    '/contentdossier/' + dossier.id
                            },
                            limit:
                                props.block.settings?.limit?.length > 0
                                    ? props.block.settings.limit
                                    : null,
                            addDossier: shouldShowAdd ? (
                                <DossierModalNewButton
                                    location={'Block'}
                                    defDossierId={props.block.def_dossier_id}
                                    onSuccess={onSuccess}
                                    onClose={() => setAdd(false)}
                                    add={add}
                                    onMax={props.onMax}
                                    size={'md'}
                                    kedo={kedo}
                                />
                            ) : null,
                            callBackPager: (value) => setPager(value),
                        }}
                        kedo={kedo}
                        forwardedRef={getCurrentRef()}
                    />
                )}
            </div>
        )
    }

    const renderTableHead = (results, listItems) => {
        if (!results || !results.items) return null

        if (!results.dossiers || results.dossiers.length <= 0) {
            return (
                <TableHead>
                    <TableRow
                        sx={{
                            '& .MuiTableCell-head, th': {
                                textAlign: 'left',
                                fontWeight: 'bold',
                            },
                        }}
                    >
                        <TableCell>
                            <Typography color="text.secondary">
                                <FontAwesomeIcon icon={faExclamationCircle} />{' '}
                                &nbsp; {kedo.t('No items found')}
                            </Typography>
                        </TableCell>
                    </TableRow>
                </TableHead>
            )
        }

        return (
            <TableHead>
                <TableRow>
                    {listItems.map((listItem) => (
                        <TableCell key={listItem.id} data-ddiid={listItem.id}>
                            {kedo.env().translateItem(listItem, 'displayitem')}
                        </TableCell>
                    ))}
                    {props.block.settings.inline_edit && <TableCell />}
                </TableRow>
            </TableHead>
        )
    }

    const getValue = (listItem, dossierData, dossier) => {
        if (!dossierData || !listItem) {
            return ''
        }

        let singleDossierData = false
        singleDossierData = dossierData.find(
            (data) => data.content.id === listItem.id
        )
        if (singleDossierData && singleDossierData.content) {
            singleDossierData = singleDossierData.content.content
        }

        if (!singleDossierData || singleDossierData.length <= 0) {
            if (listItem.def_dossier_def_field?.def_field?.type !== 'list') {
                return ''
            } else if (listItem.settings && !listItem.settings.fast_switch) {
                return ''
            }
        }

        return (
            <DisplayItem
                kedo={kedo}
                changeValue={() => {
                    //TODO
                }}
                content={singleDossierData}
                dossier={dossier}
                errors={null}
                item={listItem}
                key={listItem.id}
                mode={'list'}
            />
        )
    }

    const handleContent = (event, dossier) => {
        if (isInteractiveClick(event)) return

        if (
            !(event.target instanceof Element) ||
            !(event.currentTarget instanceof Element)
        ) {
            return false
        }

        if (event.target.closest(interactiveClickSelector)) {
            return false
        }

        if (
            !event.target.closest(contentClickSelector) &&
            !event.currentTarget.closest(contentClickSelector)
        ) {
            return false
        }

        window.location.replace(`/contentdossier/${dossier.id}`)
    }

    const renderContentRow = (dossierData, dossier, listItems) => {
        return (
            <StyledTableRow
                key={dossier.id}
                hover
                hoverActions
                variant="compact"
                verticalAlign="top"
                className={`contentRow ${
                    dossier.archived ? 'archivedDossier' : ''
                }`}
            >
                {listItems.map((listItem) => {
                    const cellContent = getValue(listItem, dossierData, dossier)
                    if (
                        listItem.def_dossier_def_field?.def_field?.type ===
                            'list' &&
                        listItem.settings?.fast_switch
                    ) {
                        return (
                            <TableCell
                                key={listItem.id}
                                className="contentData"
                            >
                                {cellContent}
                            </TableCell>
                        )
                    }
                    return (
                        <TableCell
                            key={listItem.id}
                            onClick={(event) => handleContent(event, dossier)}
                            className={'contentData'}
                            sx={{ cursor: 'pointer' }}
                        >
                            {cellContent}
                        </TableCell>
                    )
                })}
                {props.block.settings.inline_edit && (
                    <TableCell data-cell-inline-edit>
                        <DossierModalEditButton
                            dossierId={dossier.id}
                            dense
                            kedo={kedo}
                        />
                    </TableCell>
                )}
            </StyledTableRow>
        )
    }

    const renderPagination = (data) => {
        if (!pager) {
            return ''
        }

        return (
            <Pagination
                onChangePage={(page) => {
                    if (
                        props.block.settings.layout !==
                            ('cards' || 'calendar') &&
                        curRef &&
                        curRef.current
                    ) {
                        curRef.current.onChangePage(page)
                    } else {
                        setPage(page)
                    }
                }}
                kedo={kedo}
                pager={pager}
            />
        )
    }

    const renderTableContent = (results, listItems) => {
        if (!results?.dossiers || results.dossiers.length <= 0) return null

        return (
            <TableBody sx={{}}>
                {results.dossiers.map((dossier) =>
                    renderContentRow(
                        results.contents.filter(
                            (dataItem) => dataItem.id === dossier.id
                        ),
                        dossier,
                        listItems
                    )
                )}
            </TableBody>
        )
    }

    const getFilterButton = () => {
        //TODO
    }

    const renderTabsOrTable = (data) => {
        if (props.block?.settings?.tab_queries?.[0]?.length > 0) {
            return (
                <Tabs
                    value={props.block.settings.tab_queries.findIndex(
                        (t) => t.query === query
                    )}
                    onChange={(e, newValue) => {
                        setQuery(
                            props.block.settings.tab_queries[newValue].query
                        )
                        setTimeout(() => fetchData(), 50)
                    }}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    sx={horizontalScrollableTabsSx}
                >
                    {props.block.settings.tab_queries.map((item, index) => (
                        <Tab key={index} label={item.title} />
                    ))}
                </Tabs>
            )
        }

        return (
            <Box>
                <Box className="block-content">{renderData(data)}</Box>
                {pager?.pages > 1 && (
                    <Box className="block-footer">{renderPagination(data)}</Box>
                )}
            </Box>
        )
    }

    return (
        <Box className={!props.dashboard ? 'kedo-block' : null}>
            <Box
                sx={props.misc ? { height: '50px' } : {}}
                className="block-header"
            >
                <BlockToolbar
                    onClose={props.onClose}
                    block={props.block}
                    onMax={props.onMax}
                    kedo={kedo}
                    onSuccess={props.onSuccess}
                    fetchData={
                        props.block.settings.layout === ('cards' || 'calendar')
                            ? fetchData
                            : null
                    }
                />
                {props.getBlockHeader
                    ? props.getBlockHeader(props.block, props.dossier)
                    : null}
            </Box>
            {loading ? (
                <center>
                    <LoadingDefault />
                </center>
            ) : contents ? (
                props.block.settings.layout !== ('cards' || 'calendar') &&
                renderTabsOrTable(contents)
            ) : null}
        </Box>
    )
}

export default Block
